import { type Maybe, maybe, rd } from '@passionware/monads';
import { Globe } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TeamScoping } from 'v5/api/_common/query/team-scoping';
import type { WorkspaceScoping } from 'v5/api/_common/query/workspace-scoping';
import { focusRings } from 'v5/design-system/_common/focusRing';
import { Badge } from 'v5/design-system/Badge';
import { EnterpriseTeamIcon } from 'v5/features/core/enterprise/EnterpriseTeamIcon';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { cn } from 'v5/platform/dom/cn';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import type { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import type { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

/** What the scope is to the dialog: where it creates, where the entity lives, whose data it lists. */
export type ScopeBadgeMeaning = 'createsIn' | 'belongsTo' | 'lists';

export type ScopeBadgeProps = WithServices<
  [WithEnterpriseService, WithAppScopeService, WithFeatureService]
> & {
  scope: Maybe<WorkspaceScoping & Partial<TeamScoping>>;
  meaning: ScopeBadgeMeaning;
};

/**
 * Whose data a dialog reads or writes, as badges for its title — the same
 * scope {@link ScopeBreadcrumbs} names on a page, with the same workspace and
 * team icons: the workspace and, on a team scope, the team after it; the organization as "All
 * workspaces"; nothing while the scope or the names are not known. Hover or
 * focus explains what the scope means for this dialog.
 */
export function ScopeBadge({ scope, meaning, services }: ScopeBadgeProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ScopeBadge' });
  const organizationId = services.appScopeService.useOrganizationId();
  const workspaceId = scope?.workspaceId ?? null;
  const teamId = scope?.teamId ?? null;
  const workspace = rd.tryGet(
    services.enterpriseService.useWorkspace(organizationId, workspaceId)
  );
  const team = rd.tryGet(
    rd.map(
      services.enterpriseService.useTeams(
        workspaceId !== null && teamId !== null
          ? { organizationId, workspaceId }
          : maybe.ofAbsent()
      ),
      teams => teams.find(candidate => candidate.id === teamId)
    )
  );
  const isEnterprise = rd.getOrElse(
    services.featureService.useFeature(
      services.appScopeService.useOrganizationScopeIdentity(),
      'enterprise'
    ),
    false
  );
  if (!scope || !isEnterprise) return null;
  if (workspaceId === null) {
    return (
      <Explained explanation={t(`${meaning}.organization`)}>
        <Badge variant="gray" tone="subtle" size="sm" dashed>
          <Globe />
          {t('allWorkspaces')}
        </Badge>
      </Explained>
    );
  }
  if (!workspace || (teamId !== null && !team)) return null;
  return (
    <Explained
      explanation={
        team
          ? t(`${meaning}.team`, {
              workspace: workspace.name,
              team: team.name
            })
          : t(`${meaning}.workspace`, { workspace: workspace.name })
      }
    >
      <Badge variant="gray" tone="subtle" size="sm" className="gap-1.5">
        <EnterpriseWorkspaceIcon
          name={workspace.name}
          iconUrl={workspace.iconUrl}
          className="size-4"
        />
        {workspace.name}
      </Badge>
      {team ? (
        <Badge variant="gray" tone="subtle" size="sm" className="gap-1.5">
          <EnterpriseTeamIcon
            name={team.name}
            iconUrl={team.iconUrl}
            iconColor={team.iconColor}
            className="size-4"
          />
          {team.name}
        </Badge>
      ) : null}
    </Explained>
  );
}

/**
 * The badges as one focusable group: the tooltip on hover and keyboard focus,
 * the same text as its accessible description. A dialog's initial focus can
 * land here; opened by a click, that focus is not visible and keeps the
 * tooltip closed.
 */
function Explained({
  explanation,
  children
}: {
  explanation: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const hovered = useRef(false);
  const [open, setOpen] = useState(false);
  return (
    <SimpleTooltip
      title={explanation}
      side="bottom"
      open={open}
      onOpenChange={next =>
        setOpen(
          next && (hovered.current || !!ref.current?.matches(':focus-visible'))
        )
      }
    >
      <span
        ref={ref}
        tabIndex={0}
        onPointerEnter={() => (hovered.current = true)}
        onPointerLeave={() => (hovered.current = false)}
        aria-description={explanation}
        className={cn(
          'inline-flex items-center gap-2 rounded-sm',
          focusRings.focus.visible
        )}
      >
        {children}
      </span>
    </SimpleTooltip>
  );
}
